import { Plus } from 'lucide-react';
import { Button, OverflowMenu, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { Modal } from '../../Modal';
import type { SkillSummary } from '../../../lib/my-agents';
import { useSkillsModal } from './use-skills-modal';
import { SkillConnectorLine } from './SkillConnectorLine';
import styles from './SkillsModalDesktop.module.css';

// The "Agent skills" modal on desktop (Figma 12801-159044): the active-count is the modal title,
// the body a hairline-divided list of team-wide skills (name, what it does, a trash Icon Button to
// remove), and the footer carries Close and "Add new skill". The card is capped at 650px and the
// body scrolls inside it (`scrollBody`) with the DS scrollbar flush to the card's right edge.
// Behaviour is shared with the mobile sheet via `useSkillsModal`.
export function SkillsModalDesktop({ skills }: { skills: SkillSummary[] }) {
  const { t } = useTranslation();
  const { title, addSkill, removeSkill, dismiss } = useSkillsModal(skills);

  return (
    <Modal
      open
      onClose={dismiss}
      size="medium"
      maxHeightPx={650}
      labelledBy="agent-skills-title"
      title={title}
      headerSeparator
      footerSeparator
      scrollBody
      contentClassName={styles.body}
      footer={
        <>
          <Button variant="outlined" onClick={dismiss}>
            {t('common.close')}
          </Button>
          <Button startIcon={<Plus size={16} aria-hidden />} onClick={addSkill}>
            {t('agentSkills.addNew')}
          </Button>
        </>
      }
    >
      {skills.length === 0 && (
        <Typography variant="p2" color="secondary" className={styles.empty}>
          {t('agentSkills.empty')}
        </Typography>
      )}
      {skills.map((skill) => (
        <div key={skill.name} className={styles.row}>
          <span className={styles.rowText}>
            <Typography variant="heading-02" color="strong" component="h4">
              {skill.name}
            </Typography>
            {skill.hint && (
              <Typography variant="p2" color="secondary" className={styles.hint}>
                {skill.hint}
              </Typography>
            )}
            <SkillConnectorLine filename={skill.filename} />
            {skill.learnings.length > 0 && (
              <span className={styles.learnings}>
                <Typography variant="p3" color="secondary" className={styles.learnedTitle}>
                  {t('agentSkills.learnedTitle')}
                </Typography>
                {skill.learnings.map((learning) => (
                  <Typography key={learning} variant="p2" color="secondary" className={styles.learning}>
                    {`· ${learning}`}
                  </Typography>
                ))}
              </span>
            )}
          </span>
          {/* Remove is demoted to the row's overflow: a standing trash beside every skill read as the
              row's PRIMARY action, for a list whose rows are otherwise inert. */}
          <OverflowMenu
            ariaLabel={t('agentSkills.removeNamed', { skill: skill.name })}
            triggerClassName={styles.remove}
            actions={[{ key: 'remove', label: t('common.remove'), destructive: true, onSelect: () => removeSkill(skill) }]}
          />
        </div>
      ))}
    </Modal>
  );
}
